<!-- @format -->
<template>
    <div class="link-branding-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Link Branding</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="currentStep === steps.loading" class="step loading-step">
                    <loading-screen-svg />
                </div>
                <div v-else-if="currentStep === steps.domain" class="step">
                    <p>
                        By updating your DNS with these CNAME records, you'll increase
                        deliverability with your email communication as well as brand any links you
                        may use in your messaging. This step will give confidence to your recipients
                        that the links they click are safe.
                    </p>
                    <p>
                        To complete this, you'll need the ability to access and add CNAME records to
                        your DNS.
                    </p>

                    <app-input
                        v-model="linkBranding.domain"
                        label="Domain"
                        placeholder="abctech.com"
                        description="If you plan on sending from 'marketing@abctech.com', then you would enter 'abctech.com'"
                    />

                    <app-input
                        v-model="linkBranding.subdomain"
                        label="Subdomain"
                        placeholder="link"
                        description="If you would like a custom subdomain like 'link.abctech.com'"
                    />
                </div>
                <div
                    v-else-if="currentStep === steps.createRecords"
                    class="step create-records-step"
                >
                    <loading-screen-svg />
                    <p>Hold on while we create your DNS records...</p>
                </div>
                <div v-else-if="currentStep === steps.records" class="step">
                    <div v-if="linkBranding.valid === true">
                        <h4><r3-checkmark-svg /> Success!</h4>
                        <p>
                            Records verified. Your links will now be branding using
                            <strong> {{ link }} </strong>.
                        </p>
                    </div>
                    <div v-else-if="linkBranding.valid === false">
                        <h4><s3-close-svg /> Verification Error</h4>
                        <div>
                            <p>
                                First, let's see if the record is appearing, click the following
                                link:
                            </p>
                            <p>
                                <a
                                    target="_blank"
                                    :href="`https://emailstuff.org/dns/txt/${linkBranding.records[1].host}`"
                                    >https://emailstuff.org/dns/txt/{{
                                        linkBranding.records[1].host
                                    }}
                                </a>
                            </p>
                        </div>
                        <p>
                            If this page tells you that "No TXT record found for
                            {{ linkBranding.records[1].host }}", and you have added your record,
                            check your TTL value. This value - the "time to live" - is the number of
                            seconds DNS servers will cache your record for. Lowering this value will
                            make the record propagate faster.
                        </p>
                        <p>
                            If the record still doesn't propagate, you will need to talk to your DNS
                            or web host and ask them to help you out. From our side, we can only see
                            what is there, not make any changes.
                        </p>
                        <p>
                            If it looks like the record is there and correct, then contact support.
                            It will help if you mention the domain name you are trying to add
                            records for.
                        </p>
                    </div>
                    <div v-else>
                        <p>
                            Copy the following records to your DNS. For help, please refer to your
                            DNS's specific instructions on how to add a CNAME record. Make sure to
                            adjust the Time to Live (TTL) to the lowest possible setting so that we
                            can verify quickly.
                        </p>
                    </div>
                    <master-table
                        class="cname-table"
                        tableName="cname_link_branding"
                        :initialSort="initialSort"
                        initialSortDirection="asc"
                        :items="linkBranding.records"
                        :totalItems="linkBranding.records.length"
                        :searchable="false"
                        :isInitializing="loading"
                        :refreshEvents="['cname:update']"
                        @query="query($event)"
                    >
                        <master-table-row
                            slot="master-table-row"
                            slot-scope="{ item }"
                            :item="item"
                        >
                            <master-table-cell
                                slot="master-table-cell"
                                label="Type"
                                :classes="['type-cell']"
                            >
                                {{ item.type }}
                            </master-table-cell>
                            <master-table-cell
                                slot="master-table-cell"
                                label="Host"
                                :classes="['host-cell']"
                            >
                                <app-tooltip :value="item.host" />
                            </master-table-cell>
                            <master-table-cell
                                slot="master-table-cell"
                                label="Data"
                                :classes="['data-cell']"
                            >
                                <app-tooltip :value="item.data" />
                            </master-table-cell>
                        </master-table-row>
                    </master-table>

                    <div class="instructions">
                        <div class="instruction">
                            <label>GoDaddy</label>
                            <a
                                href="https://www.godaddy.com/help/add-a-cname-record-19236"
                                target="_blank"
                            >
                                https://www.godaddy.com/help/add-a-cname-record-19236
                            </a>
                        </div>
                        <div class="instruction">
                            <label>NameCheap</label>
                            <a
                                href="https://www.namecheap.com/support/knowledgebase/article.aspx/9646/2237/how-can-i-set-up-a-cname-record-for-my-domain"
                                target="_blank"
                            >
                                https://www.namecheap.com/support/knowledgebase/article.aspx/9646/2237/how-can-i-set-up-a-cname-record-for-my-domain
                            </a>
                        </div>
                        <div class="instruction">
                            <label>BlueHost</label>
                            <a href="https://my.bluehost.com/hosting/help/cname" target="_blank">
                                https://my.bluehost.com/hosting/help/cname
                            </a>
                        </div>
                        <div class="instruction">
                            <label>HostGator</label>
                            <a
                                href="https://support.hostgator.com/articles/how-to-change-dns-zones-mx-cname-and-a-records"
                                target="_blank"
                            >
                                https://support.hostgator.com/articles/how-to-change-dns-zones-mx-cname-and-a-records
                            </a>
                        </div>
                    </div>
                </div>
                <div v-if="currentStep === steps.validating" class="step validate-records-step">
                    <loading-screen-svg />
                    <p>Validating your DNS records...</p>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-button" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button :loading="currentStep.loading" @click="currentStep.action">
                        {{ currentStep.buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const MasterTable = () => import('@/components/master-table/master-table.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const S3CloseSvg = () => import('@/components/svg/grid/s3-close-svg');
const R3CheckmarkSvg = () => import('@/components/svg/grid/r3-checkmark-svg');
const AppToolTip = () => import('@/components/common/app-tooltip');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'link-branding-widget',

    components: {
        'master-table': MasterTable,
        'app-button': AppButton,
        'app-input': AppInput,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        's3-close-svg': S3CloseSvg,
        'r3-checkmark-svg': R3CheckmarkSvg,
        'app-tooltip': AppToolTip,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            linkBranding: {
                id: this.widget.linkBrandingId,
                clientId: this.widget.clientId,
                domain: null,
                subdomain: null,
                valid: null,
                records: [],
            },

            loading: true,
            currentStep: null,
            initialSort: {
                name: 'Domain',
                value: 'Domain',
            },
            steps: {
                loading: {
                    title: 'Loading...',
                    buttonText: 'Loading...',
                    action: () => {},
                    loading: true,
                },
                domain: {
                    title: 'Choose domain',
                    buttonText: 'Next',
                    action: this.createRecords,
                    loading: false,
                },
                createRecords: {
                    title: 'Creating records...',
                    buttonText: 'Loading...',
                    action: () => {},
                    loading: true,
                },
                records: {
                    title: 'Apply records',
                    buttonText: 'Validate',
                    action: this.validate,
                    loading: false,
                },
                validating: {
                    title: 'Validating...',
                    buttonText: 'Validating...',
                    action: () => {},
                    loading: true,
                },
            },
        };
    },

    computed: {
        link() {
            if (this.linkBranding.subdomain) {
                return `${this.linkBranding.subdomain}.${this.linkBranding.domain}`;
            } else {
                return this.linkBranding.domain;
            }
        },
    },

    created() {
        if (this.linkBranding.id) {
            this.currentStep = this.steps.records;
            EventBus.$emit('cname:update');
        } else {
            this.currentStep = this.steps.domain;
        }
    },

    methods: {
        validate() {
            this.currentStep = this.steps.validating;

            this.$api
                .post(`/api/LinkBrandings/${this.linkBranding.id}/Validate`)
                .then((response) => {
                    this.linkBranding.valid = response.data;

                    EventBus.$emit('link-branding:update');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while validating your DNS records.',
                        { error },
                    );
                })
                .finally(() => {
                    this.currentStep = this.steps.records;
                });
        },

        getFilterParam() {
            return `$filter=Id eq ${this.linkBranding.id}`;
        },

        getPageParam(options) {
            return `$skip=${options.itemsPerPage * (options.currentPage - 1)}`;
        },

        getTopParam(options) {
            return `$top=${options.itemsPerPage}`;
        },

        getSelectParam() {
            return '$select=IsValid,Domain,Subdomain,DomainCnameHost,DomainCnameValue,OwnerCnameHost,OwnerCnameValue';
        },

        getOrderParam(options) {
            if (options.sortProperty) {
                return `$orderby=${options.sortProperty.value} ${options.sortDirection}`;
            } else {
                return '';
            }
        },

        getQueryParams(options) {
            return [
                this.getFilterParam(options),
                this.getPageParam(options),
                this.getTopParam(options),
                this.getOrderParam(options),
                this.getSelectParam(),
                `$count=true`,
            ].join('&');
        },

        mapItems(rawData) {
            return rawData.map((item) => {
                this.linkBranding.records = [
                    {
                        type: 'CNAME',
                        host: item.DomainCnameHost,
                        data: item.DomainCnameValue,
                    },
                    {
                        type: 'CNAME',
                        host: item.OwnerCnameHost,
                        data: item.OwnerCnameValue,
                    },
                ];

                this.currentStep = this.steps.records;
            });
        },

        query(options) {
            this.loading = true;
            var query = `/service/LinkBrandings?${this.getQueryParams(options)}`;

            this.$api
                .get(query)
                .then((response) => {
                    this.linkBranding.valid = response.data.value[0].IsValid;
                    this.linkBranding.domain = response.data.value[0].Domain;
                    this.linkBranding.subdomain = response.data.value[0].Subdomain;
                    this.items = this.mapItems(response.data.value);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to fetch your link branding.', { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        createRecords() {
            this.currentStep = this.steps.createRecords;

            var request = {
                domain: this.linkBranding.domain,
                subdomain: this.linkBranding.subdomain,
                clientId: this.linkBranding.clientId,
            };

            this.$api
                .post(`/api/LinkBrandings`, request)
                .then((response) => {
                    this.linkBranding.id = response.data.Id;
                    this.linkBranding.domain = response.data.Domain;
                    this.linkBranding.subdomain = response.data.Subdomain;

                    this.linkBranding.records = [
                        {
                            type: 'CNAME',
                            host: response.data.DomainCnameHost,
                            data: response.data.DomainCnameValue,
                        },
                        {
                            type: 'CNAME',
                            host: response.data.OwnerCnameHost,
                            data: response.data.OwnerCnameValue,
                        },
                    ];

                    this.currentStep = this.steps.records;

                    EventBus.$emit('link-branding:update');
                })
                .catch((error) => {
                    this.currentStep = this.steps.domain;
                    this.$helpers.showFailNotification(
                        'Failed to create your DNS record. Please try again.',
                        { error }
                    );
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.link-branding-widget .cname-table .master-table-cell {
    height: auto;
    min-height: 52px;
}

.link-branding-widget .cname-table .type-cell {
    min-width: 100px;
    max-width: 100px;
}

.link-branding-widget .cname-table .data-cell {
    min-width: 200px;
    max-width: 200px;
}

.link-branding-widget .cname-table .host-cell {
    min-width: 400px;
    max-width: 400px;
}

.link-branding-widget .cname-table .host-cell .master-table-cell-content,
.link-branding-widget .cname-table .data-cell .master-table-cell-content {
    white-space: normal;
    overflow: visible;
    word-break: break-all;
}

.link-branding-widget .create-records-step,
.link-branding-widget .validate-records-step,
.link-branding-widget .loading-step {
    text-align: center;
}

.link-branding-widget .instructions .instruction {
    margin-top: 25px;
}

.link-branding-widget .instructions .instruction label {
    display: block;
}
</style>
